import React, { useState, useEffect, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Card, Row, Col, Typography, Avatar, Tag, Button, Input, Spin, Pagination, Skeleton, Select, Empty } from 'antd';
import { SearchOutlined, UserOutlined } from '@ant-design/icons';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { ApiDefinition } from '../../config';
import apiService from '../../services/api';
import { MenteeSearchRequest, MenteeSearchResponse, AdvertisementType } from '../../types';
import { menteeSearchQueryKeys } from '../../lib/queryClient';
import { useDebouncedValue } from '../../lib/useDebouncedValue';
import FilterSortBar from '../../components/Common/FilterSortBar';
import AdvertisementDisplay from '../../components/Common/AdvertisementDisplay';

const { Title, Text } = Typography;
const { Option } = Select;

function parseMenteeParamsFromUrl(sp: URLSearchParams): MenteeSearchRequest {
  return {
    page: sp.get('page') ? Number(sp.get('page')) : 1,
    pageSize: sp.get('pageSize') ? Number(sp.get('pageSize')) : 12,
    searchTerm: sp.get('search') || undefined,
    sortBy: sp.get('sortBy') || undefined,
    sortOrder: sp.get('sortOrder') || undefined
  };
}

const Mentees: React.FC = () => {
  const navigate = useNavigate();
  const [urlSearchParams, setUrlSearchParams] = useSearchParams();
  const { user } = useAuth();
  const isCompany = user?.role === 'Company';
  const [searchParams, setSearchParams] = useState<MenteeSearchRequest>(() =>
    parseMenteeParamsFromUrl(urlSearchParams)
  );

  useEffect(() => {
    const next = parseMenteeParamsFromUrl(urlSearchParams);
    setSearchParams(prev =>
      JSON.stringify(prev) === JSON.stringify(next) ? prev : next
    );
  }, [urlSearchParams]);

  useEffect(() => {
    const params: Record<string, string> = {};
    if (searchParams.searchTerm) params.search = searchParams.searchTerm;
    if (searchParams.sortBy) params.sortBy = searchParams.sortBy;
    if (searchParams.sortOrder) params.sortOrder = searchParams.sortOrder;
    if (searchParams.page !== 1) params.page = String(searchParams.page);
    if (searchParams.pageSize !== 12) params.pageSize = String(searchParams.pageSize);
    setUrlSearchParams(params, { replace: true });
  }, [searchParams]);

  const debouncedSearchParams = useDebouncedValue(searchParams, ApiDefinition.searchDebounceMs);

  const { data: searchResult, isLoading: searchLoading } = useQuery({
    queryKey: menteeSearchQueryKeys.search(debouncedSearchParams),
    queryFn: async () => {
      const response = await apiService.searchMentees(debouncedSearchParams);
      if (!response.success) throw new Error(response.message);
      return response.data;
    },
    staleTime: 1000 * 60 * 2, // 2 dakika
  });

  const mentees = searchResult?.data ?? [];
  const pagination = {
    current: searchResult?.pageNumber ?? 1,
    pageSize: searchResult?.pageSize ?? 12,
    total: searchResult?.totalCount ?? 0
  };

  const handleSearch = () => {
    setSearchParams(prev => ({ ...prev, page: 1 }));
  };

  const handleFilterChange = (key: string, value: any) => {
    setSearchParams(prev => ({ ...prev, [key]: value, page: 1 }));
  };

  const handlePageChange = (page: number, pageSize?: number) => {
    setSearchParams(prev => ({
      ...prev,
      page,
      pageSize: pageSize ?? prev.pageSize
    }));
  };

  const handleMenteeClick = (menteeId: number) => {
    navigate(`/mentee/${menteeId}`);
  };

  const handleResetFilters = () => {
    setSearchParams({ page: 1, pageSize: 12, sortBy: undefined, sortOrder: undefined });
  };

  const SORT_OPTIONS = [
    { value: '', label: 'Varsayılan (En yeni)' },
    { value: 'createdAt_asc', label: 'En eski' },
    { value: 'rating_desc', label: 'En yüksek puan' },
    { value: 'rating_asc', label: 'En düşük puan' },
    { value: 'name_asc', label: 'İsim (A-Z)' },
    { value: 'name_desc', label: 'İsim (Z-A)' }
  ];
  const handleSortChange = (value: string | undefined) => {
    if (!value) setSearchParams(prev => ({ ...prev, sortBy: undefined, sortOrder: undefined, page: 1 }));
    else {
      const [sortBy, sortOrder] = value.split('_');
      setSearchParams(prev => ({ ...prev, sortBy, sortOrder, page: 1 }));
    }
  };
  const currentSortValue = searchParams.sortBy && searchParams.sortOrder ? `${searchParams.sortBy}_${searchParams.sortOrder}` : '';

  const renderMenteeCard = (mentee: MenteeSearchResponse) => (
    <Card
      key={mentee.id}
      className="app-card"
      style={{ height: '100%', cursor: 'pointer' }}
      onClick={() => handleMenteeClick(mentee.id)}
      role="button"
      tabIndex={0}
      aria-label={`${mentee.firstName} ${mentee.lastName} mentee profilini görüntüle`}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          handleMenteeClick(mentee.id);
        }
      }}
    >
      <div style={{ padding: '16px', textAlign: 'center' }}>
        {/* Profil Resmi */}
        <div style={{ marginBottom: '12px', display: 'flex', justifyContent: 'center' }}>
          {mentee.profileImage ? (
            <Avatar 
              size={80} 
              src={`data:image/jpeg;base64,${mentee.profileImage}`}
              style={{ border: '3px solid var(--border-light)' }}
            />
          ) : (
            <Avatar 
              size={80} 
              icon={<UserOutlined />} 
              style={{ border: '3px solid var(--border-light)' }}
            />
          )}
        </div>
        <div style={{ marginBottom: '12px', minHeight: '50px' }}>
          <Title level={4} style={{ margin: 0, marginBottom: '4px', fontSize: 'clamp(0.95rem, 2vw, 1.1rem)' }}>
            {mentee.firstName} {mentee.lastName}
          </Title>
          {mentee.title && (
            <Text type="secondary" style={{ fontSize: 'clamp(0.8rem, 1.5vw, 0.875rem)' }}>
              {mentee.title}
            </Text>
          )}
        </div>


        {mentee.experienceYears && (
          <div style={{ marginBottom: '12px' }}>
            <Text type="secondary">{mentee.experienceYears} yıl deneyim</Text>
          </div>
        )}

        <div style={{ marginBottom: '12px' }}>
          {mentee.skills.slice(0, 3).map((skill, index) => (
            <Tag key={index} color="blue" style={{ margin: '2px' }}>
              {skill}
            </Tag>
          ))}
          {mentee.skills.length > 3 && (
            <Tag color="default">+{mentee.skills.length - 3}</Tag>
          )}
        </div>

        {mentee.bio && (
          <Text type="secondary" style={{ fontSize: 'clamp(0.75rem, 1.5vw, 0.875rem)' }}>
            {mentee.bio.length > 100 ? `${mentee.bio.substring(0, 100)}...` : mentee.bio}
          </Text>
        )}

        <div style={{ marginTop: '16px' }}>
          <Button 
            type="primary" 
            size="small"
            style={{ 
              borderRadius: '8px',
              fontWeight: '500'
            }}
          >
            Profili Gör
          </Button>
        </div>
      </div>
    </Card>
  );

  const hasActiveFilters = searchParams.sortBy != null;

  const renderFilters = () => (
    <div style={{ marginBottom: '24px' }}>
      <div className="app-hero">
        <div className="app-hero-inner">
          <Title level={1} className="app-hero-title">Menteeler</Title>
          {!isCompany && <p className="app-hero-subtitle">Kariyerlerinde ilerlemek isteyen ve mentörlük arayan profesyoneller</p>}
        </div>
      </div>

      {/* FilterSortBar */}
      <FilterSortBar
        searchPlaceholder="Mentee ara..."
        searchValue={searchParams.searchTerm || ''}
        onSearchChange={(value) => handleFilterChange('searchTerm', value)}
        onSearch={handleSearch}
        sortOptions={SORT_OPTIONS.map(o => ({ value: o.value || '', label: o.label }))}
        sortValue={currentSortValue || undefined}
        onSortChange={handleSortChange}
        onReset={handleResetFilters}
        hasActiveFilters={hasActiveFilters}
        mobileBreakpoint="md"
      />

      {/* Banner Reklam */}
      <div style={{ marginTop: '24px', marginBottom: '24px' }}>
        <AdvertisementDisplay 
          pagePath="/mentees" 
          type={AdvertisementType.Banner}
          position="top"
        />
      </div>

      {/* Sonuç sayısı */}
      {!isCompany && pagination.total > 0 && (
        <div style={{ marginTop: '16px', textAlign: 'center' }}>
          <Text type="secondary" style={{ fontSize: '14px' }}>
            {pagination.total} mentee bulundu
          </Text>
        </div>
      )}
    </div>
  );

  return (
    <div className="app-page">
      <div className="app-container">
        {renderFilters()}

        {searchLoading && mentees.length === 0 ? (
          <Row gutter={[16, 16]}>
            {Array.from({ length: 12 }).map((_, index) => (
              <Col xs={12} sm={12} md={8} lg={6} key={`skeleton-${index}`}>
                <Card className="app-card" style={{ height: '100%' }} bodyStyle={{ padding: '18px', textAlign: 'center', display: 'flex', flexDirection: 'column', height: '100%' }}>
                  <div style={{ width: '100%', marginBottom: '12px', display: 'flex', justifyContent: 'center' }}>
                    <Skeleton.Avatar active size={80} shape="circle" style={{ width: '80px', height: '80px' }} />
                  </div>
                  <div style={{ width: '100%', marginBottom: '12px' }}>
                    <Skeleton active paragraph={{ rows: 2, width: ['90%', '70%'] }} title={false} />
                  </div>
                  <div style={{ width: '100%', marginBottom: '12px', display: 'flex', justifyContent: 'center' }}>
                    <Skeleton.Button active size="small" style={{ width: '120px', height: '20px' }} />
                  </div>
                  <div style={{ width: '100%', marginBottom: '12px', display: 'flex', justifyContent: 'center', gap: '4px', flexWrap: 'wrap' }}>
                    <Skeleton.Button active size="small" style={{ width: '60px', height: '24px' }} />
                    <Skeleton.Button active size="small" style={{ width: '70px', height: '24px' }} />
                    <Skeleton.Button active size="small" style={{ width: '65px', height: '24px' }} />
                  </div>
                  <div style={{ width: '100%', marginBottom: '12px' }}>
                    <Skeleton active paragraph={{ rows: 2, width: ['100%', '90%'] }} title={false} />
                  </div>
                  <div style={{ width: '100%', marginTop: '16px', display: 'flex', justifyContent: 'center' }}>
                    <Skeleton.Button active size="small" style={{ width: '100px', height: '28px' }} />
                  </div>
                </Card>
              </Col>
            ))}
          </Row>
        ) : (
          <Spin spinning={searchLoading && mentees.length > 0}>
            <Row gutter={[16, 16]}>
              {mentees.map((mentee, index) => (
                <React.Fragment key={mentee.id}>
                  <Col xs={12} sm={12} md={8} lg={6}>
                    {renderMenteeCard(mentee)}
                  </Col>
                  {/* Her 6 mentee'den sonra card reklam */}
                  {(index + 1) % 6 === 0 && (
                    <Col xs={12} sm={12} md={8} lg={6}>
                      <AdvertisementDisplay 
                        pagePath="/mentees" 
                        type={AdvertisementType.Card}
                        position="inline"
                      />
                    </Col>
                  )}
                </React.Fragment>
              ))}
            </Row>

            {mentees.length === 0 && !searchLoading && (
              <div className="app-empty-card">
                <Empty description="Mentee bulunamadı. Arama kriterlerinizi değiştirerek tekrar deneyin." image={Empty.PRESENTED_IMAGE_SIMPLE} />
              </div>
            )}

            {pagination.total > 0 && (
              <div style={{ textAlign: 'center', marginTop: '32px' }}>
                <Pagination
                  current={pagination.current}
                  pageSize={pagination.pageSize}
                  total={pagination.total}
                  onChange={handlePageChange}
                  showSizeChanger
                  showQuickJumper
                  showTotal={(total, range) => `${range[0]}-${range[1]} / ${total} mentee`}
                />
              </div>
            )}
          </Spin>
        )}
      </div>
    </div>
  );
};

export default Mentees;